<script setup lang="ts">
    import { onMounted, reactive } from 'vue';
    import { deleteOAuthProvider, getOAuthProviders } from './OAuthProvider.access';
    import { OAuthProvider } from './OAuthProvider.types';
    import { getTriggerURI } from './OAuthProvider.helpers';
    import Loading from '@shared/components/Loading.vue';
    import { vConfirm } from '@shared/directives/VConfirm';

    const state = reactive<{
        loading: boolean;
        oauthProviders: OAuthProvider[];
    }>({
        loading: false,
        oauthProviders: [],
    });

    onMounted(async () => {
        state.loading = true;
        try {
            state.oauthProviders = await getOAuthProviders();
        } finally {
            state.loading = false;
        }
    });

    async function deleteProvider(providerId: string) {
        await deleteOAuthProvider(providerId);
        // Refresh the list after deletion
        state.oauthProviders = await getOAuthProviders();
    }

    function maskClientId(clientId: string): string {
        const length = clientId.length;
        if (length <= 4) return clientId; // No masking if the length is too short
        const visibleStart = clientId.slice(0, 2);
        const visibleEnd = clientId.slice(-2);
        const maskedPart = '*'.repeat(6);
        return `${visibleStart}${maskedPart}${visibleEnd}`;
    }
</script>

<template>
    <Loading :show="state.loading" loadingStyle="progress-bar"></Loading>
    <button type="button" class="btn btn-primary" @click="$router.push({ name: 'NewOAuthProvider' })">New provider</button>

    <template v-if="!state.loading">
        <table class="table table-hover">
            <thead>
                <tr>
                    <th>#</th>
                    <th>Provider Name</th>
                    <th>Trigger URI</th>
                    <th>Base URI</th>
                    <th>Client ID</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(provider, index) in state.oauthProviders" :key="provider.OAuthProviderId">
                    <td>{{ index + 1 }}.</td>
                    <td>{{ provider.ProviderName }}</td>
                    <td>{{ getTriggerURI(provider.ProviderName) }}</td>
                    <td>{{ provider.BaseUri }}</td>
                    <td>{{ maskClientId(provider.ClientId) }}</td>
                    <td>
                        <button
                            type="button"
                            class="btn btn-primary btn-xs"
                            @click="$router.push({ name: 'EditOAuthProvider', params: { oauthProviderId: provider.OAuthProviderId } })">
                            Edit
                        </button>

                        <button
                            type="button"
                            class="btn btn-info btn-xs btn-margin"
                            @click="$router.push({ name: 'OAuthProviderAuditLog', params: { oauthProviderId: provider.OAuthProviderId } })">
                            Audit Logs
                        </button>

                        <button v-confirm="{ action: () => deleteProvider(provider.OAuthProviderId) }" class="btn btn-danger btn-xs btn-margin">Delete</button>
                    </td>
                </tr>
            </tbody>
        </table>
    </template>
</template>

<style scoped>
    /* Add any scoped styles here */
    .btn-margin {
        margin-left: 3px;
    }
</style>
